import type { DateRange } from "react-day-picker";
import type { ComparisonTimeFrame } from "schemas/utils/analytics";

import * as React from "react";

import DeprecatedInput from "@v1/components/common/designSystem/Input";
import { useAnalyticsQueryContext } from "@v1/features/analytics/contexts/AnalyticsQueryContext";
import AppliedFiltersTags from "@v1/features/analytics/moreFilters/AppliedFiltersTags";
import BarPopover from "@v1/features/analytics/moreFilters/BarPopover";
import TriggerBarPopoverCombobox from "@v1/features/analytics/moreFilters/TriggerBarPopoverCombobox";
import { AnalyticsHostPagesCombobox } from "@v1/features/analytics/selects/AnalyticsHostPagesCombobox";
import {
  AnalyticsMainRangeCombobox,
  AnalyticsTimeRangeCombobox,
} from "@v1/features/analytics/selects/AnalyticsTimeRangeCombobox";
import AnalyticsUrlHostCombobox from "@v1/features/analytics/selects/AnalyticsUrlHostCombobox";
import useCurrentAnalyticsTab from "@v1/features/analytics/useCurrentAnalyticsTab";
import useCurrentAnalyticsTimezone from "@v1/features/analytics/useCurrentAnalyticsTimezone";
import { useOverridableInput } from "@v1/hooks/useOverridableInput";
import { COMPARE_RANGE_TIME_FRAME_OPTIONS } from "analytics-utils/analytics";
import { Search } from "lucide-react";

import PageTypeSelect from "./selects/PageTypeSelect";

type AnalyticsFiltersProps = {
  search?: {
    value: string;
    onChange: (value: string) => void;
  };
};

const AnalyticsFilters: React.FC<AnalyticsFiltersProps> = ({ search }) => {
  const { query, dispatchAnalyticsQuery } = useAnalyticsQueryContext();
  const timeZone = useCurrentAnalyticsTimezone();

  const analyticsTab = useCurrentAnalyticsTab();

  /**
   * This function only ever updates the compareAt range.
   * Either the user selects to compare with a predefined period
   * (e.g. previous-period), or the user selects to compare with a
   * custom date period.
   *
   * @author Max 2024-09-17
   */
  const handleCompareRangeChange = (
    selectedComparePeriodValue: ComparisonTimeFrame | DateRange,
  ) => {
    dispatchAnalyticsQuery({
      type: "updateCompareRange",
      payload: selectedComparePeriodValue,
    });
  };

  const searchUrlPathInputProps = useOverridableInput({
    value: search?.value ?? "",
    timeout: 1000,
    onValueChange: search?.onChange ?? (() => {}),
  });

  const [isMoreFiltersBarOpen, setIsMoreFiltersBarOpen] =
    React.useState<boolean>(false);
  const [selectedUrlParamOptionValue, setSelectedUrlParamOptionValue] =
    React.useState<string>("");

  const handleUrlParamOptionClick = (value: string) => {
    setSelectedUrlParamOptionValue(value);
    setIsMoreFiltersBarOpen(!isMoreFiltersBarOpen);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2 md:mr-2 md:flex-row md:justify-between">
        <div className="flex flex-col gap-2 md:flex-row">
          <AnalyticsMainRangeCombobox />
          <AnalyticsTimeRangeCombobox<ComparisonTimeFrame | DateRange>
            options={COMPARE_RANGE_TIME_FRAME_OPTIONS}
            placeholderPrefix="Compare to: "
            handleRangeChange={handleCompareRangeChange}
            isCompare={true}
            selectedTimePeriod={query.selectedTimePeriod}
            selectedComparePeriod={query.selectedComparePeriod}
            timeZone={timeZone}
          />
          <PageTypeSelect />
          <AnalyticsUrlHostCombobox />
          {analyticsTab === "analytics_deep_dive" && (
            <AnalyticsHostPagesCombobox />
          )}
          <TriggerBarPopoverCombobox
            handleUrlParamOptionClick={handleUrlParamOptionClick}
          />
        </div>
        {search && (
          <div className="md:w-full md:max-w-[300px]">
            <DeprecatedInput
              placeholder="Search path..."
              size="base"
              startEnhancer={<Search size={14} className="text-muted" />}
              {...searchUrlPathInputProps}
            />
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <AppliedFiltersTags />
      </div>
      <div className="absolute">
        <BarPopover
          isOpen={isMoreFiltersBarOpen}
          onOpenChange={setIsMoreFiltersBarOpen}
          selectedUrlParamOptionValue={selectedUrlParamOptionValue}
        />
      </div>
    </div>
  );
};

export default AnalyticsFilters;
